import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import LandingPage from "./LandingPage";
import StoreLocatorPage from "./StoreLocatorPage";
import BlogDetailPage from "./BlogDetailPage";
import ProductDetailPage from "./ProductDetailPage";

const SubdomainRouter = () => {
  const params = useParams<{ subdomain?: string; section?: string; slug?: string }>();

  // Extract subdomain from URL params OR hostname
  const getSubdomain = () => {
    // First check URL params (for /subdomain/:subdomain route)
    if (params.subdomain) {
      return params.subdomain;
    }

    // Then check hostname (for subdomain.localhost or subdomain.my-domain.com)
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');

      // For subdomain.localhost or subdomain.my-domain.com
      if (parts.length >= 2 && parts[0] !== 'localhost' && parts[0] !== 'www') {
        return parts[0];
      }
    }

    return null;
  };

  const subdomain = getSubdomain();

  // For subdomain.localhost/:section or subdomain.localhost/:section/:slug
  // the params will contain section and slug directly
  const section = params.section;
  const slug = params.slug;

  // Initialize from SSR data if available (BEFORE first render to avoid hydration mismatch)
  const getInitialSSRState = () => {
    if (typeof window !== 'undefined' && (window as any).__SSR_DATA__) {
      const ssrData = (window as any).__SSR_DATA__;
      return {
        subdomainType: ssrData.subdomain_type || null,
        loading: false,
        ssrData: ssrData,
      };
    }
    return {
      subdomainType: null,
      loading: true,
      ssrData: null,
    };
  };

  const initialState = getInitialSSRState();
  const [subdomainType, setSubdomainType] = useState<string | null>(initialState.subdomainType);
  const [loading, setLoading] = useState(initialState.loading);
  const [error, setError] = useState<string | null>(null);
  const [ssrDataState] = useState(initialState.ssrData);

  console.log("=== SubdomainRouter ===");
  console.log("Hostname:", typeof window !== 'undefined' ? window.location.hostname : 'SSR');
  console.log("URL:", typeof window !== 'undefined' ? window.location.href : 'SSR');
  console.log("Extracted subdomain:", subdomain);
  console.log("Params:", { subdomain: params.subdomain, section, slug });
  console.log("======================");

  useEffect(() => {
    // Check if we have SSR data first
    if (typeof window !== 'undefined' && (window as any).__SSR_DATA__) {
      const ssrData = (window as any).__SSR_DATA__;
      console.log("Using SSR data, detected subdomain type:", ssrData.subdomain_type);
      setSubdomainType(ssrData.subdomain_type);
      setLoading(false);
      return;
    }
    checkSubdomainType();
  }, [subdomain]);

  const checkSubdomainType = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check subdomain type from new subdomain_configs table
      const { data, error: fetchError } = await supabase
        .from("subdomain_configs")
        .select("subdomain_type")
        .eq("subdomain", subdomain)
        .eq("is_active", true)
        .single();

      if (fetchError || !data) {
        throw new Error("Subdomain not found");
      }

      setSubdomainType(data.subdomain_type);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Page Not Found
          </h1>
          <p className="text-gray-600">
            The page you're looking for doesn't exist.
          </p>
        </div>
      </div>
    );
  }

  // ==================================================================================
  // ROUTE ISOLATION: Routes are completely isolated between subdomain types
  // - Store Locator: Only handles / and /:location-slug
  // - Landing Page: Handles /, /section, /blog/:slug, /product/:slug
  // ==================================================================================

  // STORE LOCATOR ROUTES (subdomain_type = 'store_locator')
  if (subdomainType === "store_locator") {
    // Store locator URLs:
    // - / → Main store locator page (all locations)
    // - /:location-slug → Individual location detail page
    if (section && !slug) {
      console.log("Rendering StoreLocatorPage with location slug:", section);
      return <StoreLocatorPage locationSlug={section} />;
    }
    console.log("Rendering StoreLocatorPage without location slug");
    return <StoreLocatorPage />;
  }

  // LANDING PAGE ROUTES (subdomain_type = 'landing')
  // Blog detail route: /blog/:slug
  if (section === "blog" && slug) {
    console.log("Rendering BlogDetailPage with slug:", slug);
    return <BlogDetailPage />;
  }

  // Product detail route: /product/:slug
  if (section === "product" && slug) {
    console.log("Rendering ProductDetailPage with productId:", slug);
    return <ProductDetailPage />;
  }

  // Landing page routes:
  // - / → Home page (all sections)
  // - /reviews → Reviews section only
  // - /posts → Posts section only
  // - /blogs → Blogs section only
  // - /products-services → Products & Services section only
  // - /contact-us → Contact section only
  // - etc.
  console.log("Rendering LandingPage with activeSection:", section, "ssrData:", !!ssrDataState);
  return <LandingPage activeSection={section} ssrData={ssrDataState} subdomain={subdomain || undefined} />;
};

export default SubdomainRouter;